'use client'

import { ReactNode, useCallback, useEffect, useState } from 'react'
import { Card, Cell, Divider, Note, Spacer } from '@vinted/web-ui'

import { useSession } from '@marketplace-web/shared/session-data'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { useAbTest, useTrackAbTest } from '@marketplace-web/feature-flags/ab-tests-data'
import {
  updateUserBundleDiscounts,
  BundleDiscountModel,
  clickBundleDiscountEnableEvent,
  closeBundleDiscountScreenEvent,
} from '@marketplace-web/bundles/items-bundle-data'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import { BUNLDE_DISCOUNTS_POLICY_URL } from '../../constants/routes'
import BundleDiscountsSelectionList from './BundleDiscountsSelectionList'
import BundleDiscountsToggle from './BundleDiscountsToggle'
import useFetchInitialBundleDiscountsSettings from './hooks/useFetchInitialBundleDiscountsSettings'
import { BundleDiscountsSettingsState } from './types'

const BundleDiscountsSettings = () => {
  const translate = useTranslate('settings.bundle_discounts')
  const { track } = useTracking()
  const { user } = useSession()
  const userId = user?.id || null

  const initialSettings = useFetchInitialBundleDiscountsSettings()

  const [isUpdating, setIsUpdating] = useState(false)
  const [settings, setSettings] = useState<BundleDiscountsSettingsState>(initialSettings)

  const noteImprovementAbTest = useAbTest('dd_tx_bundle_discounts_information_note')
  const isNoteImprovementAbTestEnabled = noteImprovementAbTest?.variant === 'on'
  useTrackAbTest(noteImprovementAbTest)

  const trackPageLeave = useCallback(() => {
    if (document.visibilityState === 'hidden') return undefined

    if (!settings.isDiscountEnabled) return undefined

    const fractions =
      settings.selectedDiscounts.map(discount => discount.fraction || undefined) || undefined

    if (!fractions) return undefined

    return track(
      closeBundleDiscountScreenEvent({
        discount2Items: Number(fractions[0]) * 100 || null,
        discount3Items: Number(fractions[1]) * 100 || null,
        discount5Items: Number(fractions[2]) * 100 || null,
        screen: 'settings',
      }),
    )
  }, [settings.isDiscountEnabled, settings.selectedDiscounts, track])

  useEffect(() => {
    document.addEventListener('visibilitychange', trackPageLeave)
    window.addEventListener('beforeunload', trackPageLeave)

    return () => {
      document.removeEventListener('visibilitychange', trackPageLeave)
      window.removeEventListener('beforeunload', trackPageLeave)
    }
  }, [trackPageLeave])

  useEffect(() => {
    if (initialSettings.isLoading) return

    setSettings(initialSettings)
  }, [initialSettings])

  async function updateDiscounts({
    isEnabled,
    selectedDiscounts,
  }: {
    isEnabled: boolean
    selectedDiscounts: Array<BundleDiscountModel>
  }) {
    if (!userId) return

    setIsUpdating(true)

    await updateUserBundleDiscounts({
      userId,
      isEnabled,
      discounts: selectedDiscounts,
    })

    setIsUpdating(false)
  }

  function handleDiscountsToggleChange(isChecked: boolean) {
    setSettings(prevSettings => ({
      ...prevSettings,
      isDiscountEnabled: isChecked,
    }))

    updateDiscounts({
      isEnabled: isChecked,
      selectedDiscounts: settings.selectedDiscounts,
    })

    track(
      clickBundleDiscountEnableEvent({
        enableDiscount: isChecked,
        screen: 'settings',
      }),
    )
  }

  function isDiscountInIncreasingOrder(
    discountToValidate: BundleDiscountModel,
    referenceDiscount: BundleDiscountModel,
  ) {
    const fractionToValidate = Number(discountToValidate.fraction)
    const fractionOfReference = Number(referenceDiscount.fraction)

    const isDiscountValueLower = fractionOfReference < fractionToValidate
    const isDiscountValueBigger = fractionOfReference > fractionToValidate

    const isPositionAbove = discountToValidate.minimalItemCount < referenceDiscount.minimalItemCount
    const isPositionBelow = discountToValidate.minimalItemCount > referenceDiscount.minimalItemCount

    return (isPositionAbove && isDiscountValueBigger) || (isPositionBelow && isDiscountValueLower)
  }

  function getDiscountsOverridenInIncreasingOrder(referenceDiscount: BundleDiscountModel) {
    return settings.selectedDiscounts.map(discount => {
      if (discount.minimalItemCount === referenceDiscount.minimalItemCount) return referenceDiscount
      if (isDiscountInIncreasingOrder(discount, referenceDiscount)) return discount

      return {
        ...discount,
        fraction: referenceDiscount.fraction,
      }
    })
  }

  function handleDiscountSelection(updatedDiscount: BundleDiscountModel) {
    const selectedDiscounts = getDiscountsOverridenInIncreasingOrder(updatedDiscount)

    setSettings(prevSettings => ({
      ...prevSettings,
      selectedDiscounts,
    }))

    updateDiscounts({
      selectedDiscounts,
      isEnabled: settings.isDiscountEnabled,
    })
  }

  function renderLoader() {
    return (
      <ContentLoader size={ContentLoader.Size.Large} testId="bundle-discounts-settings-loader" />
    )
  }

  function renderContent() {
    const noteTranslationKey = settings.isDiscountEnabled ? 'note' : 'disabled_note'
    const values = {
      'faq-link': (chunks: Array<ReactNode>) => (
        <a
          href={BUNLDE_DISCOUNTS_POLICY_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="bundle-discounts-policy-url"
        >
          {chunks}
        </a>
      ),
    }

    const hintTranslation = isNoteImprovementAbTestEnabled
      ? translate(noteTranslationKey, values)
      : translate('hint')

    return (
      <>
        <Card>
          <BundleDiscountsToggle
            isDisabled={isUpdating}
            isChecked={settings.isDiscountEnabled}
            onChange={handleDiscountsToggleChange}
          />
        </Card>
        {isNoteImprovementAbTestEnabled && settings.isDiscountEnabled && <Divider />}
        {!isNoteImprovementAbTestEnabled && <Note text={hintTranslation} html />}
        {settings.isDiscountEnabled && (
          <Cell testId="bundle-discounts-selection-list" styling={Cell.Styling.Tight}>
            <Spacer />
            <Card overflow="visible">
              <BundleDiscountsSelectionList
                isDisabled={isUpdating}
                selectedDiscounts={settings.selectedDiscounts}
                discountFractions={settings.availableDiscountFractions}
                onDiscountSelection={handleDiscountSelection}
              />
            </Card>
          </Cell>
        )}
        {isNoteImprovementAbTestEnabled && <Note text={hintTranslation} html />}
      </>
    )
  }

  return settings.isLoading ? renderLoader() : renderContent()
}

export default BundleDiscountsSettings
